/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  Directive,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { coalesceObjectProperty } from '@gh/core-util';
import { filter$, first$ } from '@gh/rx/operators';
import { eq, inRange, isNil, isString, noop, remove } from 'lodash';
import { isUploadFinalEvent, UploadController, UploadEventType } from '../../../uploader';
import type { AstNode, HtmlSerializer, TinyMCE } from 'tinymce';
import { TinymceSettings } from './tinymce-settings';

declare const tinymce: TinyMCE;

export type PlaceholderID = any;

type Node = AstNode;

const WSC_CLASS_NAMES = ['wsc-spelling-problem', 'wsc-grammar-problem'];

/**
 * Cleans HTML content tree from wsc-* nodes
 * @param tree
 */
export const cleanContentTree = (tree: Node): Node => {
  const stack = [tree];

  while (stack.length) {
    const current = stack.pop()!;

    const className = current.attr('class');
    if (className && isString(className) && WSC_CLASS_NAMES.some((wscClassName) => className.includes(wscClassName))) {
      // remove node if it belongs to wsc-* node
      if (current.parent && current.firstChild) {
        stack.push(current.firstChild);
        current.parent.insert(current.firstChild, current);
        current.remove();
      }
    } else if (current.firstChild) {
      // traverse only parent elements
      let nextChild: Node | null | undefined = current.firstChild;
      while (nextChild) {
        stack.push(nextChild);
        nextChild = nextChild.next;
      }
    }
  }
  return tree;
};

@Directive({
  standalone: false,
  selector: '[ghTinymce]',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: TinymceTextareaDirective,
    multi: true,
  }],
  exportAs: 'tinymceTextarea',
})
export class TinymceTextareaDirective implements ControlValueAccessor,
                                                 OnInit,
                                                 AfterViewInit,
                                                 OnDestroy {

  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() plugins: Maybe<string>[] = [];

  innerValue: string;
  onChangeCallback: any = noop;
  onTouchedCallback: any = noop;
  editor: any;
  control: NgControl;

  private _settings?: TinymceSettings;
  private placeholders: PlaceholderID[] = [];
  private serializer: HtmlSerializer;

  constructor(private zone: NgZone,
              private injector: Injector,
              private elementRef: ElementRef,
              @Optional() @Self() private uploader?: UploadController) {
  }

  @Input('ghTinymce')
  set settings(settings: TinymceSettings) {
    this._settings = coalesceObjectProperty(settings);
  }

  ngOnInit(): void {

    if (isNil(this._settings)) {
      throw new Error('Settings is not specified for ghTinymce directive');
    }
    this.control = this.injector.get(NgControl);
  }

  ngAfterViewInit(): void {
    if (typeof tinymce === 'undefined') {
      throw new Error('TinymceTextareaDirective: tinymce is not loaded. Preload it using loadTinymce() function');
    }

    const options = {
      ...this._settings,
      target: this.elementRef.nativeElement,
      readonly: this.disabled,
      setup: (editor: any) => {
        this.editor = editor;
        editor.on('change keydown', this.keyboardIteractionsHandler(editor));
        editor.on('blur', () => this.onTouchedCallback());
// @ts-expect-error Legacy typing suppression
        if (this._settings['onInput']) {
          editor.on('input', (e: any) => {
// @ts-expect-error Legacy typing suppression
            this._settings['onInput']({event: e, editor});
          });
        }
// @ts-expect-error Legacy typing suppression
        if (this._settings['onChange']) {
          editor.on('Change', (e: any) => {
// @ts-expect-error Legacy typing suppression
            this._settings['onChange']({event: e, editor});
          });
        }
// @ts-expect-error Legacy typing suppression
        if (this._settings['onPaste']) {
          editor.on('paste', (e: any) => {
// @ts-expect-error Legacy typing suppression
            this._settings['onPaste']({event: e, editor});
          });
        }
// @ts-expect-error Legacy typing suppression
        if (this._settings['onInit']) {
          editor.on('init', (e: any) => {
// @ts-expect-error Legacy typing suppression
            this._settings['onInit'](editor);
          });
        }
// @ts-expect-error Legacy typing suppression
        if (this._settings['onLoadContent']) {
          editor.on('LoadContent', (e: any) => {
// @ts-expect-error Legacy typing suppression
            this._settings['onLoadContent'](editor);
          });
        }
// @ts-expect-error Legacy typing suppression
        if (this._settings['onMouseleave']) {
          editor.on('mouseleave', (e: any) => {
// @ts-expect-error Legacy typing suppression
            this._settings['onMouseleave'](editor);
          });
        }
        // initialize tinymce serializer.
        // It is used to serialize HTML content tree manually after it was cleaned from wsc-* nodes
        this.serializer = tinymce.html.Serializer(
          options,
          editor.schema ? editor.schema : tinymce.html.Schema(options));
      },
      init_instance_callback: (editor: any): void => {
        if (editor && this.value) {
          editor.setContent(this.value);
        }
      },
      images_upload_handler: this.imageUploadHandler.bind(this),
    };
    tinymce.init(options);
  }

  ngOnDestroy(): void {
    try {
      const editor = this.editor;
      this.editor = void 0;
      tinymce.remove(editor);
    } catch (e) {
      console.warn('Possible Tinymce race condition.');
    }
  }

  get value(): any {
    return this.innerValue;
  }

  set value(value: any) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.zone.run(() => {
        this.onChangeCallback(value);
      });

    }
  }

  insertPlaceholder(id: PlaceholderID): void {
    if (!this.placeholders.includes(id)) {
      this.placeholders.push(id);
    }
  }

  removePlaceholder(id: PlaceholderID): void {
    const { placeholders } = this;
    if (placeholders.includes(id)) {
      this.placeholders = remove(placeholders, (removeId: PlaceholderID): boolean => eq(removeId, id));
    }
  }

  getPlaceholders(): PlaceholderID[] {
    return this.placeholders;
  }

  cleanPlaceholders(): void {
    this.placeholders = [];
  }

  writeValue(value: any): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      if (this.editor) {
        this.editor.setContent(value || '');
      }
    }
  }

  registerOnChange(onChange: any): void {
    this.onChangeCallback = onChange;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.editor) {
      this.editor.mode.set(isDisabled ? 'readonly' : 'design');
    }
  }

  change(value: string): void {
    this.innerValue = value;
    this.onChangeCallback(value);
  }

  private keyboardIteractionsHandler(editor: any): (event: KeyboardEvent) => boolean | void {
    return (event: KeyboardEvent): boolean | void => {
      const element = editor.selection.getNode(); // current caret node

      if (element.classList.contains('velocity-code')) {
        if (!inRange(event.keyCode, 37, 41)) {
          event.preventDefault();
          return false;
        }
      }
      const tree = editor.getContent({ format: 'tree' });
      const content = this.serializer.serialize(cleanContentTree(tree));
      this.value = content;
    };
  }

  private imageUploadHandler(blobInfo: any, success: (url: string) => void, failure: (message: string) => void): void {
    if (this.uploader) {
      this.uploader.event$.pipe(
        filter$((event) => event.item.uploadId === item.uploadId),
        first$(isUploadFinalEvent))
        .subscribe((event) => {
          if (event.type === UploadEventType.Completed) {
            const location = event.item.location;
            if (location) {
              success(location);
            } else {
              failure('Uploading error');
            }
          } else {
            failure('Uploading error');
          }
        });

      const item = this.uploader.create({ name: blobInfo.filename(), blob: blobInfo.blob() });
      this.uploader.start(item);
    }
  }
}
